<div class="roles-container">

  <!-- ============================================================
       ✅ NO PERMISSION STATE — لو ملوش صلاحية Roles.View
       ============================================================ -->
  @if (!canViewRoles()) {
    <div class="no-permission-state">
      <div class="no-permission-icon">
        <i class="fa-solid fa-lock"></i>
      </div>
      <h2 class="no-permission-title">Access Denied</h2>
      <p class="no-permission-message">
        You don't have permission to view roles.
      </p>
      <p class="no-permission-hint">
        Please contact your administrator if you think this is a mistake.
      </p>
    </div>
  }

  <!-- ============================================================
       ✅ MAIN CONTENT — لو عنده صلاحية
       ============================================================ -->
  @if (canViewRoles()) {

    <!-- ========================= HEADER ========================= -->
    <div class="roles-header">
      <div class="title-section">
        <div class="title-icon">
          <i class="fa-solid fa-shield-halved"></i>
        </div>
        <div>
          <h1 class="roles-title">Roles & Permissions</h1>
          <span class="roles-count">{{ totalItems() }} Roles</span>
        </div>
      </div>

      <div class="header-actions">
        <div class="search-wrapper">
          <i class="fa-solid fa-magnifying-glass search-icon"></i>
          <input
            type="text"
            placeholder="Search roles..."
            class="search-input"
            [value]="searchTerm()"
            (input)="onSearch($event)"
          />
          @if (searchTerm()) {
            <button
              type="button"
              class="clear-search"
              (click)="clearSearch()"
              title="Clear search">
              <i class="fa-solid fa-xmark"></i>
            </button>
          }
        </div>

        @if (canCreateRole()) {
          <button
            type="button"
            class="add-button"
            (click)="openCreateModal()">
            <i class="fa-solid fa-plus"></i>
            <span>Create Role</span>
          </button>
        }
      </div>
    </div>

    <!-- ========================= TABLE ========================= -->
    <div class="roles-table">
      <div class="table-header">
        <div class="header-cell id-cell">
          <i class="fa-solid fa-hashtag"></i> ID
        </div>
        <div class="header-cell name-cell">
          <i class="fa-solid fa-shield-halved"></i> Role
        </div>
        <div class="header-cell">
          <i class="fa-solid fa-key"></i> Permissions
        </div>
        <div class="header-cell">
          <i class="fa-solid fa-users"></i> Users
        </div>
        <div class="header-cell">
          <i class="fa-solid fa-gear"></i> Actions
        </div>
      </div>

      <div class="table-body">
        @for (role of roles(); track role.id) {
          <div class="role-row" [class.admin-row]="isAdminRole(role)">

            <div class="cell id-cell">
              <span class="role-id">#{{ role.id }}</span>
            </div>

            <div class="cell name-cell">
              <div class="role-name-wrapper">
                <div class="role-icon" [class.admin]="isAdminRole(role)">
                  <i
                    class="fa-solid"
                    [class.fa-crown]="isAdminRole(role)"
                    [class.fa-shield]="!isAdminRole(role)">
                  </i>
                </div>

                <div class="role-info">
                  <div class="role-name">
                    {{ role.name }}
                    @if (isAdminRole(role)) {
                      <span class="system-badge">
                        <i class="fa-solid fa-lock"></i>
                        System
                      </span>
                    }
                  </div>
                  <div class="role-description">
                    {{ role.description || 'No description' }}
                  </div>
                </div>
              </div>
            </div>

            <div class="cell permissions-cell">
              <span class="count-badge permissions-badge">
                <i class="fa-solid fa-key"></i>
                {{ role.permissions_count }} Permissions
              </span>
            </div>

            <div class="cell users-cell">
              <span class="count-badge users-badge">
                <i class="fa-solid fa-user-group"></i>
                {{ role.users_count }} Users
              </span>
            </div>

            <div class="cell actions-cell">
              <div class="action-buttons">

                @if (canViewPermissions()) {
                  <button
                    type="button"
                    class="btn-action btn-permissions"
                    (click)="openPermissionsModal(role)"
                    title="Manage Permissions">
                    <i class="fa-solid fa-key"></i>
                  </button>
                }

                @if (canViewRoleUsers()) {
                  <button
                    type="button"
                    class="btn-action btn-users"
                    (click)="openUsersModal(role)"
                    title="View Users">
                    <i class="fa-solid fa-users"></i>
                  </button>
                }

                @if (canUpdateRole()) {
                  <button
                    type="button"
                    class="btn-action btn-edit"
                    (click)="openEditModal(role)"
                    title="Edit Role">
                    <i class="fa-solid fa-pen"></i>
                  </button>
                }

                @if (canDeleteRole()) {
                  <button
                    type="button"
                    class="btn-action btn-delete"
                    (click)="confirmDelete(role)"
                    [disabled]="isAdminRole(role)"
                    [title]="isAdminRole(role) ? 'Cannot delete Admin role' : 'Delete Role'">
                    <i class="fa-solid fa-trash"></i>
                  </button>
                }

                <!-- لو مفيش أي صلاحيات actions -->
                @if (!canViewPermissions() && !canViewRoleUsers() && !canUpdateRole() && !canDeleteRole()) {
                  <span class="no-actions">—</span>
                }

              </div>
            </div>

          </div>
        } @empty {
          <div class="no-roles">
            <div class="empty-icon">
              <i class="fa-solid fa-shield-halved"></i>
            </div>
            <div class="empty-title">No Roles Found</div>
            <div class="empty-description">
              Try changing your search or create a new role.
            </div>
          </div>
        }
      </div>
    </div>

    <!-- ========================= PAGINATION ========================= -->
    @if (totalPages() > 0) {
      <div class="pagination-controls">
        <div class="pagination-info">
          Showing
          <strong>{{ startIndex() }}</strong>
          to
          <strong>{{ endIndex() }}</strong>
          of
          <strong>{{ totalItems() }}</strong>
          entries
        </div>

        <div class="pagination-buttons">
          <button
            type="button"
            (click)="previousPage()"
            [disabled]="currentPage() === 1 || isLoading()"
            class="pagination-btn">
            <i class="fa-solid fa-chevron-left"></i>
            Previous
          </button>

          <div class="page-numbers">
            @for (page of getPageNumbers(); track page) {
              <button
                type="button"
                (click)="goToPage(page)"
                [class.active]="page === currentPage()"
                [disabled]="isLoading()"
                class="page-btn">
                {{ page }}
              </button>
            }
          </div>

          <button
            type="button"
            (click)="nextPage()"
            [disabled]="currentPage() === totalPages() || isLoading()"
            class="pagination-btn">
            Next
            <i class="fa-solid fa-chevron-right"></i>
          </button>
        </div>

        <div class="items-per-page">
          <label>Items per page:</label>
          <select
            [value]="itemsPerPage()"
            (change)="onItemsPerPageChange($event)"
            [disabled]="isLoading()">
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>
    }

    <!-- ========================= LOADING ========================= -->
    @if (isLoading()) {
      <div class="loading-indicator">
        <div class="spinner"></div>
        <span>Loading roles...</span>
      </div>
    }
  }
</div>


<!-- ============================================================
     CREATE / EDIT MODAL
     ============================================================ -->
@if (showRoleModal()) {
  <div class="modal-overlay" (click)="closeRoleModal()">
    <div class="modal role-modal" (click)="$event.stopPropagation()">

      <div class="modal-header">
        <div class="modal-icon" [class.edit]="isEditMode()">
          <i
            class="fa-solid"
            [class.fa-plus]="!isEditMode()"
            [class.fa-pen]="isEditMode()">
          </i>
        </div>
        <div>
          <h3 class="modal-title">{{ modalTitle() }}</h3>
          <p class="modal-subtitle">
            {{ isEditMode()
              ? 'Update role name and description'
              : 'Fill in the details to create a new role' }}
          </p>
        </div>
        <button
          type="button"
          class="modal-close"
          (click)="closeRoleModal()">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <form class="modal-body" [formGroup]="roleForm" (ngSubmit)="submitRole()">

        <div class="form-group">
          <label class="form-label">
            <i class="fa-solid fa-shield-halved"></i>
            Role Name
            <span class="required">*</span>
          </label>
          <input
            type="text"
            class="form-input"
            formControlName="name"
            placeholder="e.g., Editor, Manager, Support"
            maxlength="100"
            autocomplete="off"
          />
          @if (roleForm.get('name')?.invalid && roleForm.get('name')?.touched) {
            <span class="form-error">
              <i class="fa-solid fa-circle-exclamation"></i>
              Role name is required (max 100 characters)
            </span>
          }
        </div>

        <div class="form-group">
          <label class="form-label">
            <i class="fa-solid fa-align-left"></i>
            Description
          </label>
          <textarea
            class="form-textarea"
            formControlName="description"
            placeholder="Brief description of this role..."
            rows="3"
            maxlength="500">
          </textarea>
          <span class="form-hint">
            {{ (roleForm.get('description')?.value || '').length }} / 500
          </span>
        </div>

      </form>

      <div class="modal-footer">
        <button
          type="button"
          class="modal-btn cancel"
          (click)="closeRoleModal()"
          [disabled]="saving()">
          <i class="fa-solid fa-xmark"></i>
          Cancel
        </button>
        <button
          type="button"
          class="modal-btn confirm"
          (click)="submitRole()"
          [disabled]="saving() || roleForm.invalid">
          @if (saving()) {
            <i class="fa-solid fa-spinner fa-spin"></i>
            Saving...
          } @else {
            <i
              class="fa-solid"
              [class.fa-plus]="!isEditMode()"
              [class.fa-check]="isEditMode()">
            </i>
            {{ isEditMode() ? 'Save Changes' : 'Create Role' }}
          }
        </button>
      </div>

    </div>
  </div>
}


<!-- ============================================================
     PERMISSIONS MODAL
     ============================================================ -->
@if (showPermissionsModal()) {
  <div class="modal-overlay" (click)="closePermissionsModal()">
    <div class="modal permissions-modal" (click)="$event.stopPropagation()">

      <div class="modal-header">
        <div class="modal-icon permissions">
          <i class="fa-solid fa-key"></i>
        </div>
        <div>
          <h3 class="modal-title">
            Permissions for "{{ selectedRoleForPermissions()?.name }}"
          </h3>
          <p class="modal-subtitle">
            {{ getSelectedPermissionsCount() }} permissions selected
          </p>
        </div>
        <button
          type="button"
          class="modal-close"
          (click)="closePermissionsModal()">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <div class="modal-search">
        <i class="fa-solid fa-magnifying-glass"></i>
        <input
          type="text"
          placeholder="Search permissions..."
          [value]="permissionsSearch()"
          (input)="permissionsSearch.set($any($event.target).value)"
        />
      </div>

      <div class="modal-body permissions-body">
        @if (permissionsLoading()) {
          <div class="permissions-loading">
            <div class="spinner"></div>
            <span>Loading permissions...</span>
          </div>
        } @else if (groupedPermissions().length === 0) {
          <div class="permissions-empty">
            <i class="fa-solid fa-key"></i>
            <p>No permissions found</p>
          </div>
        } @else {
          @for (group of groupedPermissions(); track group.module) {
            <div class="permission-group">
              <div class="permission-group-header">
                <div class="group-title">
                  <input
                    type="checkbox"
                    class="group-checkbox"
                    [checked]="isGroupFullySelected(group.items)"
                    [disabled]="!canAssignPermissions()"
                    (change)="toggleGroup(group.items)"
                  />
                  <i class="fa-solid fa-folder"></i>
                  <strong>{{ group.module }}</strong>
                  <span class="group-count">
                    {{ group.selectedCount }} / {{ group.totalCount }}
                  </span>
                </div>
              </div>

              <div class="group-items">
                @for (perm of group.items; track perm.id) {
                  <label class="permission-item">
                    <input
                      type="checkbox"
                      [checked]="isPermissionSelected(perm.id)"
                      [disabled]="!canAssignPermissions()"
                      (change)="togglePermission(perm.id)"
                    />
                    <span class="permission-name">
                      {{ perm.name }}
                    </span>
                  </label>
                }
              </div>
            </div>
          }
        }
      </div>

      <div class="modal-footer">
        <button
          type="button"
          class="modal-btn cancel"
          (click)="closePermissionsModal()"
          [disabled]="saving()">
          Cancel
        </button>

        @if (canAssignPermissions()) {
          <button
            type="button"
            class="modal-btn confirm"
            (click)="savePermissions()"
            [disabled]="saving() || permissionsLoading()">
            @if (saving()) {
              <i class="fa-solid fa-spinner fa-spin"></i>
              Saving...
            } @else {
              <i class="fa-solid fa-check"></i>
              Save Permissions
            }
          </button>
        }
      </div>

    </div>
  </div>
}


<!-- ============================================================
     USERS MODAL
     ============================================================ -->
@if (showUsersModal()) {
  <div class="modal-overlay" (click)="closeUsersModal()">
    <div class="modal users-modal" (click)="$event.stopPropagation()">

      <div class="modal-header">
        <div class="modal-icon users">
          <i class="fa-solid fa-users"></i>
        </div>
        <div>
          <h3 class="modal-title">
            Users with "{{ selectedRoleForUsers()?.name }}" Role
          </h3>
          <p class="modal-subtitle">
            {{ usersTotal() }} users total
          </p>
        </div>
        <button
          type="button"
          class="modal-close"
          (click)="closeUsersModal()">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <div class="modal-search">
        <i class="fa-solid fa-magnifying-glass"></i>
        <input
          type="text"
          placeholder="Search users..."
          [value]="usersSearch()"
          (input)="onUsersSearch($event)"
        />
      </div>

      <div class="modal-body users-body">
        @if (usersLoading()) {
          <div class="permissions-loading">
            <div class="spinner"></div>
            <span>Loading users...</span>
          </div>
        } @else if (roleUsers().length === 0) {
          <div class="permissions-empty">
            <i class="fa-solid fa-users"></i>
            <p>No users found</p>
          </div>
        } @else {
          <div class="users-list">
            @for (user of roleUsers(); track user.id) {
              <div class="user-item">
                <div class="user-avatar">
                  {{ user.name.charAt(0).toUpperCase() }}
                </div>
                <div class="user-info">
                  <div class="user-name">{{ user.name }}</div>
                  <div class="user-email">{{ user.email || 'No email' }}</div>
                </div>

                <span class="user-type-badge">
                  {{ user.user_type }}
                </span>
              </div>
            }
          </div>
        }
      </div>

      <div class="modal-footer with-pagination">
        <div class="users-pagination">
          <button
            type="button"
            class="pagination-btn small"
            [disabled]="usersPageNumber() === 1 || usersLoading()"
            (click)="usersPreviousPage()">
            <i class="fa-solid fa-chevron-left"></i>
          </button>

          <span class="users-page-info">
            Page {{ usersPageNumber() }} of {{ usersTotalPages() || 1 }}
          </span>

          <button
            type="button"
            class="pagination-btn small"
            [disabled]="usersPageNumber() >= usersTotalPages() || usersLoading()"
            (click)="usersNextPage()">
            <i class="fa-solid fa-chevron-right"></i>
          </button>
        </div>

        <button
          type="button"
          class="modal-btn cancel"
          (click)="closeUsersModal()">
          Close
        </button>
      </div>

    </div>
  </div>
}


<!-- ============================================================
     DELETE MODAL
     ============================================================ -->
@if (showDeleteModal()) {
  <div class="modal-overlay" (click)="closeDeleteModal()">
    <div class="modal delete-modal" (click)="$event.stopPropagation()">

      <div class="delete-header">
        <div class="delete-icon">
          <i class="fa-solid fa-triangle-exclamation"></i>
        </div>
        <h3 class="delete-title">Delete Role?</h3>
        <p class="delete-subtitle">This action cannot be undone.</p>
      </div>

      @if (pendingDeleteRole(); as role) {
        <div class="delete-role-preview">
          <i class="fa-solid fa-shield-halved"></i>
          <div class="delete-role-info">
            <strong>{{ role.name }}</strong>
            <span>
              {{ role.permissions_count }} permissions ·
              {{ role.users_count }} users
            </span>
          </div>
        </div>
      }

      <div class="delete-warning">
        <i class="fa-solid fa-circle-info"></i>
        <p>All user assignments to this role will be removed.</p>
      </div>

      <div class="modal-footer">
        <button
          type="button"
          class="modal-btn cancel"
          (click)="closeDeleteModal()"
          [disabled]="saving()">
          Cancel
        </button>
        <button
          type="button"
          class="modal-btn danger"
          (click)="deleteRole()"
          [disabled]="saving()">
          @if (saving()) {
            <i class="fa-solid fa-spinner fa-spin"></i>
            Deleting...
          } @else {
            <i class="fa-solid fa-trash"></i>
            Delete Role
          }
        </button>
      </div>

    </div>
  </div>
}